import { z } from 'zod';
import type { AssertionSeverity } from './workflow-node-schemas.js';
import type { WorkflowHealth } from './workflows.js';
import {
  cacheCodeDurabilityAttestationSchema,
  type AssertionSpec,
  type CacheCodeDurabilityAttestation,
  type DurableTabRef,
} from './workflow-cache.js';

export * from './sandbox-egress.js';
export * from './provision-timings.js';
export * from './exploration-map.js';
export * from './exploration.js';

export type ActionInputType =
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'textarea'
  | 'select'
  | 'checkbox'
  | 'multiselect'
  | 'file';

export interface ActionInputOption {
  value: string;
  label: string;
}

export interface ActionDataRequirement {
  /**
   * Unique field identifier used to store the provided input value.
   */
  name: string;
  /**
   * Human-friendly label to display in the UI.
   */
  label: string;
  /**
   * Expected input type. Defaults to 'text' if omitted.
   */
  type?: ActionInputType;
  /**
   * Whether the field is required for the action to succeed.
   */
  required?: boolean;
  /**
   * Helpful description shown to the operator.
   */
  description?: string;
  /**
   * Placeholder text for the input.
   */
  placeholder?: string;
  /**
   * Suggested example value provided by the agent.
   */
  exampleValue?: string;
  /**
   * Optional select options for dropdown-style inputs.
   */
  options?: ActionInputOption[];
}

export type ActionInputValues = Record<string, string>;

// ── Workflow-level input definitions ──

/** Value input — a simple typed field (text, number, email, select, etc.) */
export interface WorkflowValueInputDefinition {
  kind: 'value';
  /** Variable name (snake_case, e.g. "user_email") */
  name: string;
  /** Human-friendly label */
  label: string;
  /** Expected input type. Defaults to 'text' if omitted. */
  type?: ActionInputType;
  required?: boolean;
  description?: string;
  placeholder?: string;
  /** Pre-filled default value */
  defaultValue?: string;
  options?: ActionInputOption[];
}

/** Credential input — represents a login identity (future: full credential provisioning) */
export interface WorkflowCredentialInputDefinition {
  kind: 'credential';
  /** Namespace for credential vars (e.g. "admin_user" → $var.admin_user.username) */
  name: string;
  /** Human-friendly label */
  label: string;
  required?: boolean;
  description?: string;
  /** Which credential fields this input needs */
  credentialFields: {
    username?: boolean;
    password?: boolean;
    loginUrl?: boolean;
    mfaSecret?: boolean;
  };
  /** Default credential values (for scenarios) */
  defaults?: {
    username?: string;
    password?: string;
    loginUrl?: string;
  };
  /** Default logical credential template used when a scenario does not override it */
  defaultCredentialTemplateId?: string;
}

export type WorkflowInputDefinition =
  | WorkflowValueInputDefinition
  | WorkflowCredentialInputDefinition;

// ── Workflow scenarios (named input-value sets) ──

/** Value-input overrides keyed by WorkflowValueInputDefinition.name */
export type WorkflowScenarioValueInputs = Record<string, string>;

/** Credential-input overrides keyed by WorkflowCredentialInputDefinition.name */
export type WorkflowScenarioCredentialInputs = Record<
  string,
  {
    /** Logical credential template to resolve against the run environment */
    credentialTemplateId?: string;
    /** Legacy inline fields; retained for backward-compatible imports/storage */
    username?: string;
    password?: string;
    loginUrl?: string;
    mfaSecret?: string;
  }
>;

/**
 * A named set of workflow input values — parameterized test case for a workflow.
 * Scenarios reference existing `inputsJson` entries by name; orphan keys are
 * surfaced as warnings in the UI but are tolerated in storage.
 */
export interface WorkflowScenario {
  /** Stable uuid (survives renames) */
  id: string;
  /** Human-friendly name, e.g. "Happy path" */
  name: string;
  description?: string;
  /** At most one scenario per workflow may be default; UI enforces this. */
  isDefault?: boolean;
  /**
   * When true (or undefined — defaults to true for backwards compatibility),
   * this scenario is included when the workflow runs as part of a test suite,
   * causing the suite to fan the workflow out to one run per included scenario.
   */
  includeInSuite?: boolean;
  values?: WorkflowScenarioValueInputs;
  credentials?: WorkflowScenarioCredentialInputs;
}

// ── Workflow environment eligibility ──

export type WorkflowEnvironmentType = 'permanent' | 'ephemeral' | 'local' | 'sandbox';

/**
 * Client-facing readiness of a `type='sandbox'` environment's backing
 * flow-authoring instance. Shared by the API status/ensure/heartbeat routes
 * and the web `useSandboxEnvironment` hook so the union can't drift.
 */
export type SandboxEnvironmentStatus = 'asleep' | 'warming' | 'ready' | 'stopping' | 'failed';

/** Status-route payload for a sandbox environment's backing instance. */
export interface SandboxEnvironmentState {
  status: SandboxEnvironmentStatus;
  templateId: string;
  instanceId: string | null;
  /** Raw `environment_instances.status` when an instance exists. */
  instanceStatus: string | null;
  url: string | null;
  /** Instance creation time — anchors the provisioning elapsed timer. */
  createdAt: string | null;
  expiresAt: string | null;
  /** Provisioning failure detail when `status === 'failed'`. */
  failureReason: string | null;
}

/**
 * Explicit environment eligibility constraints for a workflow.
 * Null/absent means "any" — workflow can run against any environment.
 * `only`: pin to specific environment IDs (e.g. prod smoke).
 * `envTypes`: allow only listed environment types (e.g. permanent only).
 */
export type WorkflowEnvironmentConstraints =
  | { mode: 'only'; environmentIds: string[] }
  | { mode: 'envTypes'; types: WorkflowEnvironmentType[] };

/**
 * Per-environment pass history for a workflow — "does this flow pass on this
 * env/template?". Keyed on the STABLE target environment (`workflow_runs.environment_id`),
 * so sandbox runs attribute to the persistent sandbox env, not the throwaway
 * ephemeral instance. Computed read-time over the recent terminal-run window.
 */
export interface PassByEnvironment {
  environmentId: string;
  environmentName: string;
  environmentType: WorkflowEnvironmentType;
  /**
   * Whether any terminal pass (success/flaky_success) appears in the recent
   * fetched window for this env — NOT an all-time signal. This is a read-time
   * aggregate over the same bounded window the flow-health badge uses, so a flow
   * that passed long ago but only has recent failures reads as `everPassed:false`
   * here. (The authoritative all-time "has ever passed" lives on the workflow's
   * `firstPassedAt`, which is global, not per-env.)
   */
  everPassed: boolean;
  /** Passes within the recent window. */
  passed: number;
  /** Terminal runs within the recent window. */
  total: number;
  /** classifyHealth verdict for this env's window (e.g. passing/flaky/failing). */
  health: WorkflowHealth;
}

/**
 * Metadata about input values, including whether synthetic data was used.
 * @deprecated Synthetic tracking has been removed. Form field reporting is
 * now unified via {@link ActionFormField} in debug mode only.
 */
export interface ActionInputMetadata {
  /**
   * True if any input values were synthetically generated by the agent
   * rather than provided by the user.
   */
  usedSyntheticData?: boolean;
  /**
   * List of field names that were synthetically generated.
   */
  syntheticFields?: string[];
}

/**
 * Unified form field representation for debug-mode agent reporting.
 * Combines field metadata (schema) with the value used, so the agent
 * reports everything in a single array instead of separate
 * `dataRequirements` + `inputData` fields.
 */
export interface ActionFormField {
  /** Unique field identifier (snake_case, e.g. task_title). */
  name: string;
  /** Human-friendly label from the UI. */
  label: string;
  /** Input type. Defaults to 'text' if omitted. */
  type?: ActionInputType;
  /** Whether the field is required. */
  required?: boolean;
  /** The value entered/used for this field. */
  value: string;
  /** Select/multiselect options, if applicable. */
  options?: ActionInputOption[];
}

const PLAYBOOK_STEP_AGENT_ACTIONS = [
  'click',
  'fill',
  'fill_form',
  'select',
  'navigate',
  'wait',
  'scroll',
  'hover',
  'upload',
  'verify',
  'press_key',
  'drag',
  'resize',
  'capture',
  'extract_table',
  'python_execute',
  'verify_download',
  'verify_download_content',
  'screenshot',
  'agent_loop',
] as const;

export const PLAYBOOK_STEP_ACTIONS = [...PLAYBOOK_STEP_AGENT_ACTIONS, 'call'] as const;

export type PlaybookStepAction = (typeof PLAYBOOK_STEP_ACTIONS)[number];
export type LegacyPlaybookStepAction =
  | PlaybookStepAction
  | 'other'
  | 'handle_native_dialog'
  | 'toggle'
  | 'focus';

function isPositiveIntegerString(value: string | undefined): value is string {
  if (!value || !/^\d+$/.test(value)) return false;
  return Number(value) > 0;
}

/**
 * Zod schema for the agent-settable fields of a playbook step. This is the
 * single source of truth for what the LLM can author via the
 * `updateWorkflowStep` tool — `apps/api` imports this directly instead of
 * keeping a parallel hard-coded schema (which used to drift, e.g. duplicate
 * action enum and missing `expectedDialogs`).
 *
 * Internal fields the *runtime* attaches (actionRefsSource, screenshotS3Key)
 * are NOT in this schema — they belong on the wider {@link PlaybookStep}
 * type below. Keeping them out keeps the agent's input surface tight and
 * prevents prompt injection of internal-only fields.
 *
 * Field descriptions are LLM-tuned: they are read directly into the tool's
 * function-calling schema. Don't shorten them without considering the agent.
 */
function buildActionDescription(includeRecipeFields: boolean): string {
  return (
    'The type of browser interaction. PREFER DETERMINISTIC VERBS even when verbose — three deterministic steps beat one agentic step, because deterministic steps replay via Playwright with no agent dispatch. ' +
    'Each replayable playbook step is one atomic intent executed in one exposed browser tool call and corresponds to zero or one recorded actionRef. Draw boundaries at stable page states: fuse actions coupled through ephemeral UI state into that call; split unrelated intents and every stable intermediate into separate steps. If you cannot accomplish the intent with the browser tools you have, do not add a playbook step for it; report the limitation outside playbookSteps. Do not summarize several recorded tool calls into one "other" step; report the same 1:1 browser primitives you actually used. One fill_form call is one atomic intent even when it fills multiple fields. ' +
    'Use "verify" for assertion checks — both user-declared inline assertions and agent-authored guardrails/completion checks that keep execution on the rails. Set assertionId to the inline assertion UUID only when accounting for that declared assertion; otherwise use "completion". Extra completion checks are not user assertion results. ' +
    'Use "verify" for inspection language such as "inspect whether", "confirm", "observe", or "check" when the result is visible in the DOM. Use "capture" instead only when the observed value is needed by later steps. ' +
    'Use "fill" for single-field inputs. Use "fill_form" when browser_fill_form was called to fill multiple fields at once — one fill_form step per call. ' +
    'Use "click" for checkboxes, switches, toggle buttons, and click-to-focus interactions. There is no separate "toggle" or "focus" action. ' +
    'Use "upload" for file upload actions, including generated CSV/file, attach file, choose file, import file, and document/image attachment intents (browser_action action="upload"). Set params.path to the staged attachment handle, generated file variable, or filename reference used by the upload tool (e.g. {path: "$var.csv_file"}), and include actionRefs pointing to the recorded file_upload action. Do NOT use "other" for file attachment/upload/import intents. ' +
    'Use "scroll" only when scrolling is the user-visible action being tested; routine browser_scroll calls for visibility, inspection, or video framing are observation steps and should usually be omitted from playbookSteps. ' +
    'Use "press_key" for keyboard actions (Enter, Escape, Tab, arrows). ' +
    'Use "press_key" with params.key="Escape" to dismiss a popover, dropdown, panel, or modal when Escape was the recorded action; use "click" if the recorded action clicked a close button or outside target. There is no separate "dismiss" action. ' +
    'Use "drag" for drag-and-drop interactions. ' +
    'Use "resize" when browser_resize was used to resize the viewport. Set params.width and params.height to positive integer pixel values and include the actionRef pointing to the recorded resize action. ' +
    'Use "capture" to extract app-generated values (IDs, codes, URLs) for later steps via $var.name, but only from a visible DOM target or the current URL. Do not capture from performance/network timing, storage, hidden DOM, or app-global state. ' +
    'Use "screenshot" only when browser_screenshot itself is the requested evidence capture. Set params.label when a label was used, optionally params.fullPage="true". Do not use screenshot for routine visual checks after actions; use verify when a browser_assert proves visible state. ' +
    'Use "extract_table" when browser_extract_table was used to extract tabular data from the page. ' +
    'Use "python_execute" when python_execute was used with cache_label to run a data validation script. ' +
    'Use "verify_download" when browser_download(action="list") / browser_list_downloads proves a file was downloaded. params.pattern is an optional filename glob (supports *, ?); omit it when any newly downloaded file is acceptable. ' +
    'Use "verify_download_content" when browser_download(action="read") / browser_read_download content proves CSV headers, PDF text, exported rows, or similar downloaded-file content. Set params.contains or params.pattern to the expected content predicate. ' +
    'Use "agent_loop" ONLY when the action set is genuinely unknown until runtime — variable count of items (e.g. "toggle row checkboxes until 3 selected"), conditional retry that depends on UI state, or a runtime predicate over non-DOM state. Set params.goal to a one-sentence checkable predicate (e.g. params.goal = "3 grid row checkboxes are selected"). DO NOT use agent_loop for: known-N iteration (write N deterministic steps), single click that might double-fire (use "click"), retry-on-flake (the runtime handles that). agent_loop steps replay via a Playwright override authored after first run; they are non-deterministic on first run, so the bar is high. ' +
    (includeRecipeFields
      ? 'When recipe fields are available, use "call" for one completed call_recipe invocation. Treat the recipe as one atomic transaction: report one call step for the whole invocation, never its internal browser actions as additional playbook steps. '
      : '') +
    'Network/devtools/API inspections and unsupported browser environment mutations are diagnostic-only unless there is an exposed browser tool for the action. If visible page state proves the condition, record that as "verify"; otherwise omit the diagnostic observation from playbookSteps. ' +
    'Do not use "other". If no concrete browser-tool-backed action above fits, omit the observation from playbookSteps and report the limitation outside the playbook.'
  );
}

const playbookStepAgentObjectSchema = z.object({
  order: z.number().describe('Step number (1-based).'),
  action: z.enum(PLAYBOOK_STEP_AGENT_ACTIONS).describe(buildActionDescription(false)),
  target: z
    .string()
    .describe(
      'Human-readable element description using visible labels, placeholders, or aria-labels. ' +
        'Examples: "\'Submit\' button", "\'Email\' input field", "\'Status\' dropdown". ' +
        'For upload actions, describe the file input and include the attachment name (e.g. "CSV file input for \'testing-checklist.csv\'"). ' +
        'Never use coordinates, ref IDs, or internal identifiers.'
    ),
  params: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      'Data values using $var.name references and $MACRO() expressions — NEVER hard-coded literals. ' +
        'Common keys: value (for fill: {value: "$EMAIL()"}), option (for select: {option: "Admin"} — ' +
        'fixed app constants like dropdown options are allowed as literals), url (for navigate: {url: "$var.target_url"}), ' +
        'path (for upload: {path: "$var.file_path"}), key (for press_key: {key: "Enter"}, {key: "Escape"}), ' +
        'width and height (for resize: {width: "390", height: "844"}), ' +
        'label and fullPage (for screenshot: {label: "seats-table", fullPage: "true"}), ' +
        'pattern or contains (for download verification: {pattern: "report-*.csv"} or {contains: "Expected Header"}), ' +
        'expression + saveAs (for capture: prefer a visible DOM ref expression or "() => window.location.href"; do not use performance.getEntries() or other network/storage/global state; e.g. {saveAs: "order_confirmation_id"}). ' +
        'For fill_form: keys are field names from the fill_form call, values are $var.name or $MACRO() (e.g. {company: "$var.company", email: "$EMAIL()"}). ' +
        'For call: keys are the recipe argument names and values are strings using the same $var.name / $MACRO() conventions. The compiler pins and inlines the recipe as one atomic transaction. ' +
        'Available macros: $UUID(), $EMAIL(), $FIRST_NAME(), $LAST_NAME(), $PHONE(), $RANDOM_STRING(n), ' +
        '$RANDOM_INT(min,max), $TODAY(), $DATE(days), $NOW(), $TIMESTAMP(), $SEQUENCE("name").'
    ),
  note: z
    .string()
    .optional()
    .describe(
      'Timing or quirk notes ONLY (e.g. "Wait 1s for modal animation", "Scroll into view first"). ' +
        'Do NOT put data values, optional markers, or verification notes here.'
    ),
  actionRefs: z
    .array(z.number())
    .max(1)
    .optional()
    .describe(
      'Zero or one recorded browser action index that corresponds to this playbook step. ' +
        'Interaction tools, browser_resize, capture evaluates (browser_evaluate with saveAs), and browser_assert produce [Action #N] tags — collect those indices here. ' +
        'Read-only browser_snapshot and plain browser_evaluate without saveAs do NOT produce action indices. browser_scroll and browser_screenshot may produce action indices when they are the primary requested tool call; omit actionRefs when no action tag was emitted. ' +
        'If you took multiple cacheable UI actions, report multiple 1:1 playbook steps instead of putting multiple refs on one step or summarizing them as "other". ' +
        'A fill_form call is a single action — use one fill_form playbook step with one actionRef. ' +
        'For a click that triggers a native dialog, keep one click actionRef on the click step and set expectedDialogs rather than adding a separate dialog actionRef. ' +
        'Actions not referenced by any playbook step are considered exploratory and will be discarded by the cache compiler.'
    ),
  expectedDialogs: z
    .enum(['accept', 'dismiss'])
    .optional()
    .describe(
      'Set when this step is known to fire a native browser dialog (alert/confirm/prompt) — ' +
        'e.g. clicking a "Delete" button that prompts "Are you sure?". On replay the runtime ' +
        'pre-arms the dialog handler with this action automatically. In free-form runs, you ' +
        'should call the dialog tool (browser_dialog for consolidated tools, browser_handle_dialog for legacy tools) with this action immediately before the step. ' +
        'Do not add a separate dialog step — keeping the pre-arm on the triggering step keeps the ' +
        'pre-arm and the triggering action in one place.'
    ),
  assertionId: z
    .string()
    .optional()
    .describe(
      'REQUIRED on verify steps when this node declares inline assertions: the inline assertion ' +
        'UUID from the node config (the exact (id=<uuid>) shown in the prompt) that this verify ' +
        "step proves, or 'completion' for an agent-authored guardrail or completion assertion proving the action succeeded. " +
        'A completion guardrail is not tied to, and must not be reported as, a user-declared assertion. ' +
        'Must equal the inlineAssertionId passed to browser_assert for this check. Never set on ' +
        'non-verify steps.'
    ),
});

type PlaybookStepAgentRefinementInput = Omit<
  z.infer<typeof playbookStepAgentObjectSchema>,
  'action'
> & {
  action: PlaybookStepAction;
  recipeRef?: { slug: string; version: number };
};

/** Actions that can bind an `assertionId` — the verify family. */
const VERIFY_BINDING_ACTIONS = new Set<PlaybookStepAction>([
  'verify',
  'verify_download',
  'verify_download_content',
]);

const playbookStepAgentSuperRefine = (
  step: PlaybookStepAgentRefinementInput,
  includeRecipeFields: boolean,
  ctx: z.RefinementCtx,
  requireVerifyBinding = false
): void => {
  if (includeRecipeFields && step.action === 'call') {
    if (!step.recipeRef) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recipeRef'],
        message:
          'call steps must pin the invoked recipe with recipeRef.slug and recipeRef.version.',
      });
    }
    if (step.target.trim().length < 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['target'],
        message:
          'call steps must keep a non-trivial human-readable target as the fallback instruction.',
      });
    }
  }
  if (step.action === 'agent_loop') {
    const goal = step.params?.goal;
    if (!goal || goal.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['params', 'goal'],
        message:
          'agent_loop steps must set params.goal to a one-sentence checkable predicate (e.g. "3 grid row checkboxes are selected").',
      });
    }
  }
  if (step.action === 'verify_download_content') {
    const pattern = step.params?.pattern;
    const contains = step.params?.contains;
    if ((!pattern || pattern.trim().length === 0) && (!contains || contains.trim().length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['params'],
        message:
          'verify_download_content steps must set params.contains or params.pattern for the expected downloaded content.',
      });
    }
  }
  if (step.action === 'resize') {
    const width = step.params?.width;
    const height = step.params?.height;
    if (!isPositiveIntegerString(width)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['params', 'width'],
        message: 'resize steps must set params.width to a positive integer pixel value.',
      });
    }
    if (!isPositiveIntegerString(height)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['params', 'height'],
        message: 'resize steps must set params.height to a positive integer pixel value.',
      });
    }
  }
  if (step.assertionId !== undefined && !VERIFY_BINDING_ACTIONS.has(step.action)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['assertionId'],
      message: 'assertionId is only valid on verify steps.',
    });
  }
  if (
    requireVerifyBinding &&
    VERIFY_BINDING_ACTIONS.has(step.action) &&
    step.assertionId === undefined
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['assertionId'],
      message:
        "verify steps must bind the assertion they prove via assertionId — use the inline assertion UUID from the prompt or 'completion'.",
    });
  }
};

/** Max length of an agent-reported `cacheCode` snippet (chars) — mirrors the L2 lane. */
export const MAX_PLAYBOOK_CACHE_CODE_LEN = 8000;

/**
 * Per-node deactivation flag, stored INSIDE the node's `configJson` (key
 * `deactivated: true`) rather than as a table column. A deactivated node is
 * skipped at execution time (its run step records status `skipped`), letting
 * authors converge a flow node-by-node from the front without running the
 * whole thing.
 *
 * Why configJson and not a column: workflow sync (`syncDraftState`) and
 * `.cofactor.md` spec compilation both carry nodes as `{nodeType, configJson}`
 * only — a top-level column would be silently wiped on every sync. The key is
 * also deliberately OUTSIDE every cache-hash whitelist (`extractHashInput`,
 * `STRUCTURE_RELEVANT_CONFIG_KEYS`) so toggling it never invalidates cache
 * rows — do not add it to either.
 */
export function isNodeConfigDeactivated(configJson: unknown): boolean {
  return (
    typeof configJson === 'object' &&
    configJson !== null &&
    (configJson as { deactivated?: unknown }).deactivated === true
  );
}

/**
 * Build the agent-facing playbook step schema.
 *
 * `includeCacheCode` adds the fast-drive `cacheCode` report lane: the minimal
 * deterministic Playwright snippet that reproduces the step, used by the cache
 * mint to compile code-backed entries when the interactions themselves were
 * driven via `browser_run_code` (whose synthetic actions never enter the
 * replay cache). The lane must ONLY be enabled for the minimal (fast-drive)
 * toolset — ref-based runs report `actionRefs` and have no way to produce
 * trustworthy step code (see the L2 `buildStepProgressSchema` counterpart in
 * `apps/api/src/workflows/cache/recovery-agent/playbook-runner-schemas.ts`).
 *
 * `includeRecipeFields` adds the atomic recipe-call authoring lane: the `call`
 * action plus its pinned `recipeRef`. The persisted {@link PlaybookStep} type
 * always accepts those fields; this option gates only what an agent can emit.
 *
 * `requireVerifyBinding` makes `assertionId` mandatory on verify-family steps
 * (`verify`, `verify_download`, `verify_download_content`) — the node declares
 * inline assertions, so every verify step must name which one it proves (or
 * `'completion'`). Cross-step uniqueness of `assertionId` (no two steps
 * claiming the same inline assertion) is validated at the array level in
 * `workflowStepSchema.ts`, not here — a per-step `superRefine` cannot see
 * sibling steps.
 */
export function buildPlaybookStepAgentSchema(opts?: {
  includeCacheCode?: boolean;
  includeRecipeFields?: boolean;
  requireVerifyBinding?: boolean;
}) {
  const recipeAwareBase = opts?.includeRecipeFields
    ? playbookStepAgentObjectSchema.extend({
        action: z.enum(PLAYBOOK_STEP_ACTIONS).describe(buildActionDescription(true)),
        recipeRef: z
          .object({
            slug: z.string().trim().min(1),
            version: z.number().int().positive(),
          })
          .optional()
          .describe(
            'Required for action="call": the exact slug and version returned by the invoked recipe. ' +
              'A call is one atomic transaction, so pin the recipe once on the call step and do not ' +
              'also report its internal browser actions as playbook steps.'
          ),
      })
    : playbookStepAgentObjectSchema;
  const base = opts?.includeCacheCode
    ? recipeAwareBase.extend({
        cacheCode: z
          .string()
          .trim()
          .min(1)
          .max(MAX_PLAYBOOK_CACHE_CODE_LEN)
          .optional()
          .describe(
            'Fast-drive only: the MINIMAL self-contained Playwright snippet (uses `page`, and the ' +
              '`vars` / `macro()` / `attach()` bindings) that reproduces THIS completed atomic intent ' +
              'from its stable entry state — the deterministic replacement for actionRefs when you ' +
              'drove the step via browser_run_code. Fuse actions coupled through ephemeral UI state: ' +
              'keep a visibility-guarded opener with the terminal action. If one call batched unrelated ' +
              "intents, report only this intent's acting lines; omit scouting and observation. " +
              'Never hardcode data/secrets — use `vars.x` and `await macro("EMAIL")`. ' +
              'Never use cacheCode for action="call": recipeRef pins the whole recipe as one atomic ' +
              'transaction and the compiler inlines its versioned code. ' +
              'For action="capture" driven through browser_run_code, the snippet must top-level ' +
              'return the captured string so replay can bind params.saveAs; omit cacheCode for a ' +
              'capture performed with the dedicated capture tool. Omit for steps performed with ' +
              'other dedicated tools (verify/navigate/upload) and for ' +
              'steps that cannot be reduced to a stable snippet. ' +
              'Must be self-contained — replays in an isolated scope; re-declare every variable/locator; ' +
              'only page/fetch/vars/macro/attach/dialog exist.'
          ),
        cacheCodeDurability: cacheCodeDurabilityAttestationSchema
          .optional()
          .describe(
            'Fast-drive only: structured evidence for a genuinely unavoidable positional locator ' +
              'in cacheCode. Omit for semantic locators, coordinates, and direct read-only ' +
              'diagnostics. This permits a normal prove pass; it never marks code verified.'
          ),
        cacheable: z
          .literal(false)
          .optional()
          .describe(
            'Fast-drive only: set false only when this step is genuinely non-deterministic or ' +
              'cannot be reduced to a stable replay artifact. This records an intentional cache gap; ' +
              'do not use it for ordinary locator difficulty or a tool call you can cite with actionRefs.'
          ),
        uncacheableReason: z
          .string()
          .trim()
          .min(1)
          .max(500)
          .optional()
          .describe(
            'Fast-drive only: required when cacheable=false. Briefly identify the transient, randomized, ' +
              'external, or otherwise non-deterministic boundary that prevents durable replay.'
          ),
      })
    : recipeAwareBase;
  return base.superRefine((step, ctx) => {
    playbookStepAgentSuperRefine(
      step,
      opts?.includeRecipeFields === true,
      ctx,
      opts?.requireVerifyBinding === true
    );
    if (
      'cacheCodeDurability' in step &&
      step.cacheCodeDurability &&
      !('cacheCode' in step && step.cacheCode)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['cacheCodeDurability'],
        message: 'cacheCodeDurability requires cacheCode on the same step',
      });
    }
    if ('cacheable' in step && step.cacheable === false) {
      const uncacheableReason =
        'uncacheableReason' in step && typeof step.uncacheableReason === 'string'
          ? step.uncacheableReason
          : undefined;
      if (!uncacheableReason?.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['uncacheableReason'],
          message: 'uncacheableReason is required when cacheable is false',
        });
      }
      if (
        ('cacheCode' in step && step.cacheCode) ||
        step.actionRefs?.length ||
        ('recipeRef' in step && step.recipeRef)
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['cacheable'],
          message:
            'cacheable:false is an intentional gap and cannot also carry cacheCode, actionRefs, or recipeRef',
        });
      }
    } else if ('uncacheableReason' in step && step.uncacheableReason) {
      ctx.addIssue({
        code: 'custom',
        path: ['uncacheableReason'],
        message: 'uncacheableReason requires cacheable:false on the same step',
      });
    }
  });
}

/** Default agent-facing playbook step schema (no fast-drive cacheCode lane). */
export const playbookStepAgentSchema = buildPlaybookStepAgentSchema();

/** Inferred type of the agent-authored fields of a playbook step. */
export type PlaybookStepAgentInput = z.infer<typeof playbookStepAgentSchema>;

/**
 * A single step in a workflow playbook.
 *
 * Composed of {@link PlaybookStepAgentInput} (everything the agent can set
 * via `updateWorkflowStep`) plus internal fields the runtime attaches during
 * compilation/healing. Splitting these keeps the agent's input surface
 * tight while letting the runtime carry provenance + screenshot keys.
 */
export interface PlaybookStep extends Omit<PlaybookStepAgentInput, 'action'> {
  /** Legacy persisted rows may still contain `other`; new agent-authored steps cannot. */
  action: LegacyPlaybookStepAction;

  /** Exact interaction-recipe version pinned by an atomic `call` step. */
  recipeRef?: { slug: string; version: number };

  /**
   * Provenance of `actionRefs`:
   *   `'recording'` (default when omitted) — emitted by the initial recording
   *     compilation; transient session-specific indices that should be stripped
   *     after the cache row is built.
   *   `'healing'` — emitted by the heal agent during cache-failure recovery and
   *     proven correct by replay. These should be preserved across compiles so
   *     the next compile lands the entry as `matchStrategy='actionRef'`.
   */
  actionRefsSource?: 'recording' | 'healing';
  /**
   * Fast-drive report lane ONLY: the minimal deterministic Playwright snippet
   * the agent reported for this step (see {@link buildPlaybookStepAgentSchema}).
   * Consumed in-memory by the cache mint to compile a code-backed entry; it is
   * STRIPPED at every persist boundary (node config writes,
   * `sanitizeStepsForCache`) — the durable home for step code is the cache
   * row's `actions_json` (`run_code` action) + `compiled_entries`, exactly as
   * on the L2 lane.
   */
  cacheCode?: string;
  /**
   * Compile-only structured evidence for a narrow positional durability
   * exception. Stripped with `cacheCode`; its bound durable home is the
   * synthesized `CachedAction.durabilityAttestation`.
   */
  cacheCodeDurability?: CacheCodeDurabilityAttestation;
  /** Fast-drive report explicitly classified this step as an intentional cache gap. */
  cacheable?: false;
  /** Required rationale when `cacheable === false`. */
  uncacheableReason?: string;
  /**
   * Layer 4 cache-durable multi-tab replay (fast-drive `cacheCode` lane only):
   * durable descriptor of the tab this step ran on. Threaded onto the
   * synthesized `run_code` `CachedAction.activeTab` at compile time so step-driven
   * replay re-resolves the active page before running the step's snippet. Absent
   * ⇒ single-tab provenance ⇒ replay is unchanged.
   */
  activeTab?: DurableTabRef;
  /** S3 key for screenshot captured after this playbook step completed. */
  screenshotS3Key?: string;
  /**
   * Legacy run-mode provenance retained so historical playbooks deserialize.
   * New writes omit this field; cache sanitization still strips either value.
   */
  playbookSource?: 'production' | 'validation';
  /**
   * Binds this verify step to the inline assertion it proves: either an id
   * from `configJson.inlineAssertions[].id` on this node, or the `'completion'`
   * sentinel for the implicit action-succeeded check. Deliberately excluded
   * from node-hash inputs (`extractHashInput`, `STRUCTURE_RELEVANT_CONFIG_KEYS`)
   * — it identifies which assertion a step proves, not the node's structure.
   */
  assertionId?: string;
}

/**
 * Bind replay verify steps to configured assertions without relying on result order.
 * Explicit assertion identities are authoritative; legacy unbound steps may match
 * unique condition/label text or the sole available assertion.
 */
export function matchAssertionSpecsToVerifySteps(
  playbookSteps: readonly PlaybookStep[],
  specs: readonly AssertionSpec[]
): Record<number, AssertionSpec> | undefined {
  const verifySteps = playbookSteps
    .filter((playbookStep) => playbookStep.action === 'verify')
    .sort((a, b) => a.order - b.order);
  if (verifySteps.length === 0 || specs.length === 0) return undefined;

  const claimedSpecs = new Set<AssertionSpec>();
  const byOrder: Record<number, AssertionSpec> = {};
  for (const playbookStep of verifySteps) {
    const candidates = specs.filter((spec) => {
      if (claimedSpecs.has(spec)) return false;
      if (playbookStep.assertionId !== undefined) {
        return spec.inlineAssertionId === playbookStep.assertionId;
      }
      const target = playbookStep.target.trim();
      return spec.condition.trim() === target || spec.label?.trim() === target;
    });
    const spec =
      candidates.length === 1
        ? candidates[0]
        : playbookStep.assertionId === undefined && verifySteps.length === 1 && specs.length === 1
          ? specs[0]
          : undefined;
    if (!spec || claimedSpecs.has(spec)) continue;
    claimedSpecs.add(spec);
    byOrder[playbookStep.order] = spec;
  }
  return Object.keys(byOrder).length > 0 ? byOrder : undefined;
}

/**
 * Execution-time snapshot of one playbook step, persisted into
 * `workflow_run_steps.metadataJson.executedPlaybook` when step-driven cache
 * replay ran for a step (full hit or partial replay before agent fallback).
 *
 * Deliberately slim: only the display-relevant agent-authored fields. Runtime
 * plumbing (`actionRefs`, `actionRefsSource`, `screenshotS3Key`,
 * `playbookSource`, `expectedDialogs`) is stripped so run rows stay small.
 * Values are sanitized back to `$var.`/`$MACRO` tokens before persistence —
 * the cache row is mutable, so this snapshot is the only exact record of what
 * replayed for a historical run.
 */
export interface ExecutedPlaybookStep {
  /** Step number (1-based) — joins against `StepOutcome.stepOrder`. */
  order: number;
  action: LegacyPlaybookStepAction;
  /** Human-readable element description. */
  target: string;
  /** Data values, with variable references preserved as `$var.`/`$MACRO` tokens. */
  params?: Record<string, string>;
  /** Optional human context authored alongside the step. */
  note?: string;
  /** Exact configured assertion identity for a bound verify step. */
  assertionId?: string;
}

/**
 * An assertion attached directly to an action node.
 * Verified after the action completes; cacheable assertions are replayed mechanically.
 */
export interface InlineAssertion {
  /** Unique ID within this node */
  id: string;
  /** Freeform description of what to verify */
  condition: string;
  /** fatal = stop the test now; fail = mark the run failed but continue; warn = log and continue */
  severity: AssertionSeverity;
  /** When true, requires agent evaluation (e.g. computer vision) and cannot be cached */
  agentic?: boolean;
}

export type BrowserMode = 'headed' | 'headless';

export * from './crawler/analysis.js';
export * from './api-catalog.js';
export * from './credentials.js';
export * from './test-sign-in.js';
export * from './test-sign-in-setup-prompt.js';
export * from './environment-kinds.js';
export * from './automation-alerts.js';
export * from './external-test.js';
export * from './exploratory-effort.js';
export * from './review-flavors.js';
// The `$var.USER.*` / `$var.ENV.*` grammar an external runner's `envMapping`
// speaks. Shared because the API materializes the `.env` from it and the web
// config editor authors it — a client-side copy of a security-critical
// allowlist grammar is exactly the drift worth designing out.
export * from './runner-env-mapping.js';
// The run-progress SSE vocabulary, shared between the emitter and the UI that
// subscribes to it — a hand-mirrored client copy drifts silently.
export * from './external-test-events.js';
export * from './runbook-run-events.js';
export * from './git-source.js';
export * from './secret-redaction.js';
export * from './trace-uploads.js';
export * from './qa/testing.js';
export * from './qa/events.js';
export * from './issues.js';
export * from './pagination.js';
export * from './workflows.js';
export * from './visual-review.js';
export * from './visual-text.js';
export * from './digests.js';
export * from './login-host.js';
export * from './notifications.js';
export * from './run-target.js';
export * from './run-prestart.js';
export * from './browser-stream.js';
export * from './attachments.js';
export * from './permissions.js';
export * from './workflow-cache.js';
export * from './cache-action-contract.js';
export * from './cache-optimizer-events.js';
export * from './cache-telemetry.js';
export * from './instance-logs.js';
export * from './local-browser.js';
export * from './benchmark.js';

export * from './api-sequence.js';
export * from './diagnostics.js';
export * from './diagnostics-filters.js';
export * from './conversation-stream.js';
export * from './sandbox-tasks.js';
export * from './sandbox-instance-size.js';
export * from './routes/index.js';
export * from './macros.js';
export * from './recording.js';
export * from './variable-utils.js';
export * from './node-outputs.js';
export * from './sandbox-action-node.js';
export * from './mcp-node.js';
export * from './credential-output.js';
export * from './action-output.js';
export * from './adhoc-test.js';
export * from './mentions.js';
export * from './element-hints.js';
export * from './ai-routing.js';
export * from './workflow-node-schemas.js';
export * from './node-type-labels.js';
export * from './visual-checkpoint.js';
export * from './visual-api.js';
export * from './browser-viewport.js';
export * from './browser-engine.js';
export * from './identifier-stability.js';
export * from './workflow-review.js';
export * from './fast-video.js';
export * from './user-features.js';
export * from './product-map.js';
export * from './password.js';
export * from './announcements.js';
export * from './assistant-pages.js';
export * from './assistant-onboardings.js';
export * from './readable-slug.js';

export { calculatePassRate, formatPassRate } from './pass-rate.js';
export * from './proof-block.js';
export * from './proof.js';
export * from './proof-body.js';
export * from './billing.js';
export * from './agent-feedback.js';
